import React from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import MainLayout from '../../../../../components/layout/MainLayout';
import { getBackofficeSidebarItems } from '../../../shell/backofficeSidebar';
import { useAuth } from '../../../../../context/AuthContext';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:3001';

type TestimonialRow = {
  id: string;
  quote: string;
  authorName: string;
  authorMeta?: string | null;
  isActive: boolean;
  sortOrder: number;
  sourceType: 'CUSTOMER_FEEDBACK' | 'MANUAL';
  customerFeedbackId?: string | null;
};

type FeedbackRow = {
  id: string;
  customerName?: string | null;
  customerEmail: string;
  subject?: string | null;
  message: string;
  createdAt: string;
};

export default function WorkspaceTestimonialsPage() {
  const location = useLocation();
  const { t } = useTranslation();
  const { authorizedFetch } = useAuth();
  const globalAuthorizedFetch = React.useCallback(
    (input: RequestInfo | URL, init: RequestInit = {}) => {
      const headers = new Headers(init.headers || {});
      headers.set('x-department-slug', '');
      return authorizedFetch(input, { ...init, headers });
    },
    [authorizedFetch],
  );
  const [items, setItems] = React.useState<TestimonialRow[]>([]);
  const [busy, setBusy] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [success, setSuccess] = React.useState<string | null>(null);

  const [inbox, setInbox] = React.useState<FeedbackRow[]>([]);
  const [inboxBusy, setInboxBusy] = React.useState(false);
  const [manual, setManual] = React.useState({
    quote: '',
    authorName: '',
    authorMeta: '',
  });

  const load = React.useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await globalAuthorizedFetch(`${API_BASE}/settings/testimonials`);
      if (!res.ok) throw new Error('load');
      const data = (await res.json()) as TestimonialRow[];
      setItems(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Unable to load testimonials.');
    } finally {
      setBusy(false);
    }
  }, [globalAuthorizedFetch]);

  const loadInbox = React.useCallback(async () => {
    setInboxBusy(true);
    try {
      const res = await globalAuthorizedFetch(`${API_BASE}/customer/feedback/inbox?scope=ALL&type=FEEDBACK`);
      if (!res.ok) return;
      const data = (await res.json()) as FeedbackRow[];
      setInbox(data.slice(0, 80));
    } finally {
      setInboxBusy(false);
    }
  }, [globalAuthorizedFetch]);

  React.useEffect(() => {
    void load();
    void loadInbox();
  }, [load, loadInbox]);

  const save = async (next: TestimonialRow[]) => {
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const payload = next.map((x, idx) => ({
        id: x.id,
        quote: x.quote,
        authorName: x.authorName,
        authorMeta: x.authorMeta ?? null,
        isActive: x.isActive,
        sortOrder: Number.isFinite(Number(x.sortOrder)) ? Number(x.sortOrder) : idx,
        sourceType: x.sourceType,
        customerFeedbackId: x.customerFeedbackId ?? null,
      }));
      const res = await globalAuthorizedFetch(`${API_BASE}/settings/testimonials`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: payload }),
      });
      if (!res.ok) throw new Error('save');
      setSuccess('Saved.');
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Unable to save.');
    } finally {
      setSaving(false);
    }
  };

  const addManual = () => {
    const quote = manual.quote.trim();
    const authorName = manual.authorName.trim();
    const authorMeta = manual.authorMeta.trim();
    if (!quote || !authorName) return;
    const next: TestimonialRow[] = ([
      ...items,
      {
        id: '',
        quote,
        authorName,
        authorMeta: authorMeta || null,
        isActive: true,
        sortOrder: items.length,
        sourceType: 'MANUAL' as const,
        customerFeedbackId: null,
      },
    ] as TestimonialRow[]).slice(0, 10);
    setManual({ quote: '', authorName: '', authorMeta: '' });
    void save(next);
  };

  const promoteFeedback = (f: FeedbackRow) => {
    const quote = (f.message || '').trim();
    const authorName = (f.customerName || '').trim() || 'Customer';
    const authorMeta = f.subject?.trim() || null;
    const next: TestimonialRow[] = ([
      ...items,
      {
        id: '',
        quote,
        authorName,
        authorMeta,
        isActive: true,
        sortOrder: items.length,
        sourceType: 'CUSTOMER_FEEDBACK' as const,
        customerFeedbackId: f.id,
      },
    ] as TestimonialRow[]).slice(0, 10);
    void save(next);
  };

  const move = (idx: number, dir: -1 | 1) => {
    const next = [...items];
    const j = idx + dir;
    if (j < 0 || j >= next.length) return;
    const a = next[idx];
    next[idx] = next[j];
    next[j] = a;
    // normalize sortOrder
    const normalized = next.map((x, k) => ({ ...x, sortOrder: k }));
    setItems(normalized);
    void save(normalized);
  };

  const toggleActive = (idx: number) => {
    const next = items.map((x, i) => (i === idx ? { ...x, isActive: !x.isActive } : x));
    setItems(next);
    void save(next);
  };

  return (
    <MainLayout withSidebar sidebarItems={getBackofficeSidebarItems(t)} activePath={location.pathname}>
      <main className="workspace-page">
        <section>
          <h1 className="workspace-page-title">
            Storefront testimonials
          </h1>
          <p className="workspace-page-subtitle text-slate-600">
            Choose up to 10 testimonials to show on the homepage carousel. You can promote customer feedback or add manual entries.
          </p>
        </section>

        <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
          {busy ? <p className="text-sm text-slate-600">Loading…</p> : null}
          {error ? <p className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p> : null}
          {success ? <p className="mb-3 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">{success}</p> : null}

          <h2 className="text-sm font-semibold text-slate-900">Selected testimonials</h2>
          <p className="mt-1 text-xs text-slate-600">Order matters. Toggle off to hide. The carousel shows active items only.</p>

          <div className="mt-4 space-y-3">
            {items.length === 0 ? (
              <p className="text-sm text-slate-600">No testimonials yet.</p>
            ) : (
              items.map((x, idx) => (
                <div key={x.id || `new-${idx}`} className="rounded-xl border border-slate-200 p-4">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-slate-900">{x.authorName}</p>
                      {x.authorMeta ? <p className="text-xs text-slate-500">{x.authorMeta}</p> : null}
                    </div>
                    <div className="flex items-center gap-2">
                      <button type="button" className="rounded-lg border px-2 py-1 text-xs" onClick={() => move(idx, -1)}>
                        ↑
                      </button>
                      <button type="button" className="rounded-lg border px-2 py-1 text-xs" onClick={() => move(idx, 1)}>
                        ↓
                      </button>
                      <button
                        type="button"
                        className={`rounded-lg border px-2 py-1 text-xs ${x.isActive ? 'bg-emerald-50 text-emerald-800' : 'bg-slate-50 text-slate-700'}`}
                        onClick={() => toggleActive(idx)}
                      >
                        {x.isActive ? 'Active' : 'Hidden'}
                      </button>
                    </div>
                  </div>
                  <p className="mt-3 text-sm text-slate-700">{x.quote}</p>
                  <p className="mt-2 text-[11px] text-slate-500">
                    Source: {x.sourceType}{x.customerFeedbackId ? ` (${x.customerFeedbackId})` : ''}
                  </p>
                </div>
              ))
            )}
          </div>

          <div className="mt-8 grid gap-6 lg:grid-cols-2">
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
              <h3 className="text-sm font-semibold text-slate-900">Add manual testimonial</h3>
              <div className="mt-3 grid gap-3">
                <label className="text-xs font-semibold text-slate-700">
                  Quote
                  <textarea
                    rows={4}
                    className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                    value={manual.quote}
                    onChange={(e) => setManual((p) => ({ ...p, quote: e.target.value }))}
                  />
                </label>
                <div className="grid gap-3 sm:grid-cols-2">
                  <label className="text-xs font-semibold text-slate-700">
                    Author name
                    <input
                      className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                      value={manual.authorName}
                      onChange={(e) => setManual((p) => ({ ...p, authorName: e.target.value }))}
                    />
                  </label>
                  <label className="text-xs font-semibold text-slate-700">
                    Meta (optional)
                    <input
                      className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                      value={manual.authorMeta}
                      onChange={(e) => setManual((p) => ({ ...p, authorMeta: e.target.value }))}
                    />
                  </label>
                </div>
                <button
                  type="button"
                  disabled={saving || items.length >= 10}
                  onClick={addManual}
                  className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
                >
                  Add
                </button>
                <p className="text-[11px] text-slate-500">Max 10 items.</p>
              </div>
            </div>

            <div className="rounded-xl border border-slate-200 bg-white p-4">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <h3 className="text-sm font-semibold text-slate-900">Promote customer feedback</h3>
                  <p className="mt-1 text-xs text-slate-600">Pick a feedback message to feature on the homepage.</p>
                </div>
                <button
                  type="button"
                  onClick={() => void loadInbox()}
                  className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs font-semibold text-slate-800 hover:bg-slate-100"
                >
                  Refresh
                </button>
              </div>
              {inboxBusy ? <p className="mt-3 text-sm text-slate-600">Loading…</p> : null}
              <div className="mt-3 space-y-3">
                {inbox.slice(0, 10).map((f) => (
                  <div key={f.id} className="rounded-xl border border-slate-200 p-3">
                    <p className="text-xs font-semibold text-slate-900">
                      {(f.customerName || '').trim() || f.customerEmail}
                    </p>
                    <p className="mt-1 line-clamp-3 text-sm text-slate-700">{f.message}</p>
                    <button
                      type="button"
                      disabled={saving || items.length >= 10}
                      onClick={() => promoteFeedback(f)}
                      className="mt-2 rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50"
                    >
                      Add to carousel
                    </button>
                  </div>
                ))}
                {inbox.length === 0 && !inboxBusy ? (
                  <p className="text-sm text-slate-600">No feedback found.</p>
                ) : null}
              </div>
            </div>
          </div>
        </section>
      </main>
    </MainLayout>
  );
}

